import { useAuth } from "@/src/contexts/auth.context";
import { FeatureRequest } from "@/src/models/feature-request.model";
import { useTheme } from "@/src/styles/theme-provider";
import * as Clipboard from "expo-clipboard";
import { ExportSquare } from "iconsax-react-nativejs";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Linking, Platform, Pressable, Text } from "react-native";
import Toast from "react-native-toast-message";

const DOUBLE_CLICK_MS = 250;

type FeatureRequestIssueBadgeProps = Readonly<{
  request: Pick<FeatureRequest, "issueNumber" | "projectUrl">;
}>;

export default function FeatureRequestIssueBadge({
  request: { issueNumber, projectUrl },
}: FeatureRequestIssueBadgeProps) {
  const { isSuperAdmin } = useAuth();
  const { themeVars } = useTheme();
  const { t } = useTranslation();
  const pendingOpen = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!issueNumber) return null;
  const issueLabel = `#${issueNumber}`;

  if (!isSuperAdmin || !projectUrl) {
    return (
      <Text className="text-xs font-semibold text-slate">{issueLabel}</Text>
    );
  }

  const copyLink = async () => {
    await Clipboard.setStringAsync(projectUrl);
    Toast.show({ type: "success", text1: t("LABEL_COPIED") });
  };

  const handlePress = () => {
    if (pendingOpen.current) {
      clearTimeout(pendingOpen.current);
      pendingOpen.current = null;
      void copyLink();
      return;
    }
    pendingOpen.current = setTimeout(() => {
      pendingOpen.current = null;
      Linking.openURL(projectUrl).catch(() => {});
    }, DOUBLE_CLICK_MS);
  };

  const webContextMenu =
    Platform.OS === "web"
      ? {
          onContextMenu: (event: { preventDefault: () => void }) => {
            event.preventDefault();
            void copyLink();
          },
        }
      : {};

  return (
    <Pressable
      onPress={handlePress}
      {...webContextMenu}
      className="flex-row items-center gap-1 px-2 py-0.5 rounded-full hover:bg-ash-3"
      accessibilityRole="link"
      accessibilityLabel={t("LABEL_OPEN_FEATURE_REQUEST_ON_GITHUB")}
    >
      <Text className="text-xs font-semibold text-primary">{issueLabel}</Text>
      <ExportSquare
        size={14}
        color={themeVars["--color-primary-default"] as string}
      />
    </Pressable>
  );
}
